/**
 * SeoHead ,  per-route dynamic <head> injection (Iter 56).
 *
 * Every public route renders <SeoHead route="/pricing" /> (or omits `route`
 * on the homepage and it defaults to "/"). Fetches the current meta from
 * /api/seo/meta/:route and injects title, description, canonical, OG, and
 * Twitter card tags via react-helmet-async.
 *
 * Sane hardcoded defaults so nothing renders empty before the seed lands
 * or the fetch resolves.
 */
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  "/": {
    title: "Acquigine: Find more deals. Structure smarter. Close faster.",
    description:
      "Acquisition intelligence platform. AI-scored opportunities from public records: businesses, multifamily, land. Six-dimension scoring, outreach tools with pacing controls, ready-to-send LOIs.",
  },
  "/pricing": {
    title: "Pricing: Acquigine",
    description:
      "Free, Pro ($99/mo), and Business ($299/mo) plans. Start free with 5 saved opportunities. Pro adds outreach + docs. Business adds bulk CSV, webhook, and GraphQL.",
  },
  "/privacy": {
    title: "Privacy Policy: Acquigine",
    description:
      "How Acquigine collects, stores, and uses data. Licensed data provider list, public-records sources, and subscriber data handling.",
  },
  "/terms": {
    title: "Terms of Service: Acquigine",
    description:
      "Acquigine Terms of Service. Acquigine is not a broker and does not receive commissions on deals.",
  },
  "/compliance": {
    title: "Compliance: Acquigine",
    description:
      "Licensed data providers, no scraping, CAN-SPAM/TCPA/DNC/litigator screening on outreach, full audit trail.",
  },
  "/sell": {
    title: "Sell your business, property, or land: Acquigine",
    description:
      "Submit your business, multifamily, or land to the Acquigine buyer network of ranked acquirers with creative-finance appetite.",
  },
  "/submit-business": {
    title: "Submit a business for sale: Acquigine",
    description:
      "List your owner-operated business for acquirers open to seller financing and creative structures.",
  },
  "/submit-land": {
    title: "Submit land or acreage: Acquigine",
    description:
      "List raw land or acreage to acquirers running creative-finance structures.",
  },
  "/submit-multifamily": {
    title: "Submit a multifamily property: Acquigine",
    description:
      "List a multifamily property to acquirers open to seller-carry, master-lease, and creative structures.",
  },
};

function defaultOgImage() {
  return `${BACKEND}/static/og/default.png`;
}

export default function SeoHead({ route = "/", jsonLd = null, children = null }) {
  const key = route in DEFAULTS ? route : "/";
  const fallback = DEFAULTS[key];

  const [meta, setMeta] = useState({
    title: fallback.title,
    description: fallback.description,
    og_image_url_absolute: defaultOgImage(),
    canonical_url: `${BACKEND}${route}`,
  });
  const [verification, setVerification] = useState("");

  useEffect(() => {
    // Fetch live meta from the backend seo_meta collection
    const cleanRoute = route === "/" ? "" : route.replace(/^\//, "");
    axios
      .get(`${BACKEND}/api/seo/meta/${cleanRoute}`)
      .then((r) => setMeta((prev) => ({ ...prev, ...r.data })))
      .catch(() => {
        // keep defaults on failure ,  never leave the head empty
      });
    // Google Search Console verification tag lives on every page but the
    // homepage is the canonical location.
    if (route === "/") {
      axios
        .get(`${BACKEND}/api/seo/verification`)
        .then((r) => setVerification(r.data.google_verification || ""))
        .catch(() => {});
    }
  }, [route]);

  const title = meta.title || fallback.title;
  const description = meta.description || fallback.description;
  const ogImage = meta.og_image_url_absolute || defaultOgImage();
  const canonical = meta.canonical_url || `${BACKEND}${route}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Acquigine" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonical} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="Acquigine" />

      {/* Google Search Console verification (homepage only, admin-configurable) */}
      {route === "/" && verification && (
        <meta name="google-site-verification" content={verification} />
      )}

      {/* JSON-LD structured data (routes may pass an array or a single object) */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}

      {children}
    </Helmet>
  );
}
